import { useState, useEffect, useRef } from "react";
import {
  useConnect,
  useDisconnect,
  useAccount,
  useSignTypedData,
  useChainId,
} from "wagmi";
import { ethers } from "ethers";
import { toSatoshi } from "../sdk/utils/math";
import { mormSigner } from "../sdk";
import { detectWallet, isPhantomAvailable, isOneKeyAvailable, getBestWallet as getBestWalletName } from "../sdk/utils/extdetection";
import { 
  isMetaMaskProviderAvailable, 
  isBitgetProviderAvailable, 
  getBitgetProvider, 
  waitForBitgetWallet,
  isTokenPocketProviderAvailable, 
  getTokenPocketProvider, 
  waitForTokenPocketWallet,
  isTokenPocketEthereumAvailable as isTokenPocketEthereumAvailableHook,
  isTokenPocketSolanaAvailable as isTokenPocketSolanaAvailableHook,
  isCoinbaseWalletProviderAvailableSync,
  getCoinbaseWalletProvider,
  waitForCoinbaseWallet,
  isCoinbaseWalletSolanaAvailable as isCoinbaseWalletSolanaAvailableHook,
  waitForBinanceWallet,
  getBinanceProvider,
} from "../hooks";
import { 
  connectPhantomSolana, 
  getPhantomSolanaProvider,
  isPhantomSolanaAvailable,
  signSolanaMessage,
  connectOneKeySolana,
  getOneKeySolanaProvider,
  signOneKeySolanaMessage,
  isBinanceSolanaAvailable,
  getBinanceSolanaProvider,
  connectBinanceSolana,
  signBinanceSolanaMessage,
  isBitgetSolanaAvailable,
  getBitgetSolanaProvider,
  connectBitgetSolana,
  signBitgetSolanaMessage,
  isTokenPocketSolanaAvailable,
  getTokenPocketSolanaProvider,
  connectTokenPocketSolana,
  signTokenPocketSolanaMessage,
  isCoinbaseWalletSolanaAvailable,
  getCoinbaseWalletSolanaProvider,
  connectCoinbaseWalletSolana,
  signCoinbaseWalletSolanaMessage,
} from "../sdk/signing/solana";
import {
  getBitcoinAddress,
  getPreferredBitcoinWallet,
  isBitcoinWalletAvailable,
  isOKXWalletInstalled,
  signBitcoinMessage,
  detectBitcoinWallets
} from "../sdk/signing/bitcoin";
import {
  isOKXWalletAvailable as isOKXWalletSDKAvailable,
  isOKXEthereumAvailable as isOKXEthereumSDKAvailable,
  isOKXSolanaAvailable as isOKXSolanaSDKAvailable,
  isOKXBitcoinAvailable as isOKXBitcoinSDKAvailable,
  signOKXEthereumTypedData,
  signOKXSolanaMessage,
  signOKXBitcoinMessage,
  getOKXEthereumAddress,
  getOKXSolanaAddress,
  getOKXBitcoinAddress,
  getOKXSolanaProvider,
} from "../sdk/signing/okx";
import {
  isOneKeySolanaAvailable as isOneKeySolanaSDKAvailable,
  isOneKeyBitcoinAvailable as isOneKeyBitcoinSDKAvailable,
  isOneKeyEthereumAvailable as isOneKeyEthereumSDKAvailable,
  signOneKeyBitcoinMessage,
  signOneKeyEthereumTypedData,
  getOneKeyBitcoinAddress,
} from "../sdk/signing/onekey";
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpChangeLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankExtractToBucket,
  OpBankWithdrawFromBucket,
  OpBankMint,
  OpBankTransfer,
} from "../sdk/types";

// Operation type definitions
type OperationType =
  | "placeOrder"
  | "cancelOrder"
  | "modifyOrder"
  | "changeLeverage"
  | "closePosition"
  | "swap"
  | "changeMarginRatio"
  | "internTransfer"
  | "bucketTransfer"
  | "extractToBucket"
  | "withdrawFromBucket"
  | "bankMint"
  | "bankTransfer";

interface OperationConfig {
  label: string;
  action: string;
  fields: FieldConfig[];
}

interface FieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "select";
  placeholder?: string;
  options?: string[];
  required?: boolean;
  autoGenerate?: boolean;
  convertToSatoshi?: boolean;
}

const OPERATION_CONFIGS: Record<OperationType, OperationConfig> = {
  placeOrder: {
    label: "Place Order",
    action: OpSubmitOrder,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "side",
        label: "Side",
        type: "select",
        options: ["BUY", "SELL"],
        required: true,
      },
      {
        key: "orderType",
        label: "Order Type",
        type: "select",
        options: ["LIMIT", "MARKET"],
        required: true,
      },
      {
        key: "price",
        label: "Price",
        type: "number",
        placeholder: "100.5",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "quantity",
        label: "Quantity",
        type: "number",
        placeholder: "1.0",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  cancelOrder: {
    label: "Cancel Order",
    action: OpCancelOrder,
    fields: [
      { key: "orderId", label: "Order ID", type: "text", required: true },
      { key: "marketId", label: "Market ID", type: "text", required: true },
    ],
  },
  modifyOrder: {
    label: "Modify Order",
    action: OpModifyOrder,
    fields: [
      { key: "orderId", label: "Order ID", type: "text", required: true },
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "newPrice",
        label: "New Price",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "newQuantity",
        label: "New Quantity",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  changeLeverage: {
    label: "Change Leverage",
    action: OpChangeLeverage,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "newLeverage",
        label: "New Leverage",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  closePosition: {
    label: "Close Position",
    action: OpClosePosition,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "quantity",
        label: "Quantity",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "marketPrice",
        label: "Market Price",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  swap: {
    label: "Swap",
    action: OpSwap,
    fields: [
      { key: "fromToken", label: "From Token", type: "text", required: true },
      { key: "toToken", label: "To Token", type: "text", required: true },
      {
        key: "amountIn",
        label: "Amount In",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "minAmountOut",
        label: "Min Amount Out",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  changeMarginRatio: {
    label: "Change Margin Ratio",
    action: OpChangeMarginRatio,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "positionId", label: "Position ID", type: "text", required: false },
    ],
  },
  internTransfer: {
    label: "Internal Transfer",
    action: OpInternTransfer,
    fields: [
      {
        key: "fromAccount",
        label: "From Account",
        type: "text",
        required: true,
      },
      { key: "toAccount", label: "To Account", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "tokenAddress",
        label: "Token Address",
        type: "text",
        required: true,
      },
    ],
  },
  bucketTransfer: {
    label: "Bucket Transfer",
    action: OpBankBucketTransfer,
    fields: [
      {
        key: "fromBucketId",
        label: "From Bucket ID",
        type: "text",
        required: true,
      },
      {
        key: "toBucketId",
        label: "To Bucket ID",
        type: "text",
        required: true,
      },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  extractToBucket: {
    label: "Extract to Bucket",
    action: OpBankExtractToBucket,
    fields: [
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  withdrawFromBucket: {
    label: "Withdraw from Bucket",
    action: OpBankWithdrawFromBucket,
    fields: [
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  bankMint: {
    label: "Bank Mint",
    action: OpBankMint,
    fields: [
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  bankTransfer: {
    label: "Bank Transfer",
    action: OpBankTransfer,
    fields: [
      { key: "to", label: "To Address", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
};

function FunctionalSigner(): JSX.Element {
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { address: wagmiAddress, isConnected: wagmiIsConnected } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const chainId = useChainId();

  // Step 1: Operation selection
  const [selectedOperation, setSelectedOperation] =
    useState<OperationType | null>(null);

  // Step 1.5: Connection method selection
  const [connectionMethod, setConnectionMethod] = useState<"wallet" | "mnemonic" | null>(null);

  // Step 2: Form fields
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [mnemonic, setMnemonic] = useState<string>(
    "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
  );
  const [ownerAddress, setOwnerAddress] = useState<string>("");
  const [nonce, setNonce] = useState<string>("");
  const [deadline, setDeadline] = useState<string>("");
  const [availableAddresses, setAvailableAddresses] = useState<string[]>([]);
  // Chain type for wallet connection (ethereum, solana, or bitcoin)
  const [connectionChainType, setConnectionChainType] = useState<"ethereum" | "solana" | "bitcoin">("ethereum");

  // Step 3: Signing
  const [signingResult, setSigningResult] = useState<{
    success: boolean;
    signature?: string;
    typedData?: any;
    error?: string;
  } | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSigningWasm, setIsSigningWasm] = useState(false);

  // Wallet detection - comprehensive multi-wallet support
  const isMetaMaskAvailable = isMetaMaskProviderAvailable();
  const isBitgetAvailable = isBitgetProviderAvailable();
  const isTokenPocketAvailable = isTokenPocketProviderAvailable();
  const isTokenPocketEthereumDetected = isTokenPocketEthereumAvailableHook();
  const isTokenPocketSolanaDetected = isTokenPocketSolanaAvailableHook();
  const isCoinbaseWalletEthereumDetected = isCoinbaseWalletProviderAvailableSync();
  const isCoinbaseWalletSolanaDetected = isCoinbaseWalletSolanaAvailableHook();

  // Rainbow Wallet detection
  const isRainbowAvailable =
    typeof window !== "undefined" &&
    (window as any).ethereum?.isRainbow === true;

  // Phantom detection: exclude OneKey masquerading as Phantom
  const isPhantomDetected =
    typeof window !== "undefined" &&
    (!!(window as any).phantom?.ethereum ||
      !!(window as any).ethereum?.isPhantom) &&
    !((window as any).$onekey || (window as any).ethereum?.isOneKey);

  const isOneKeyDetected =
    typeof window !== "undefined" &&
    (!!(window as any).$onekey?.ethereum ||
      !!(window as any).ethereum?.isOneKey ||
      !!(window as any).$onekey);

  // OneKey multi-chain detection
  const isOneKeyEthereumAvailableSDK = isOneKeyEthereumSDKAvailable();
  const isOneKeySolanaAvailable = isOneKeySolanaSDKAvailable();
  const isOneKeyBitcoinAvailable = isOneKeyBitcoinSDKAvailable();

  const isOKXEthereumAvailable =
    typeof window !== "undefined" &&
    (!!(window as any).okxwallet?.ethereum ||
      !!(window as any).ethereum?.isOKX);

  // Binance wallet detection with inpage polling support
  const [isBinanceAvailable, setIsBinanceAvailable] = useState(() => {
    if (typeof window === "undefined") return false;
    if ((window as any).binancew3w?.isExtension === true) return true;
    if ((window as any).BinanceChain) return true;
    const ethereum = (window as any).ethereum;
    if (ethereum?.isBinance) return true;
    if (ethereum) {
      const providerName = ethereum.providerName || ethereum.name;
      if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
        return true;
      }
    }
    if (ethereum?._postMessageToMp &&
        typeof ethereum._postMessageToMp === "function" &&
        ethereum?.namespace === "eip155") {
      return true;
    }
    if (isBinanceSolanaAvailable()) return true;
    return false;
  });

  // Poll for Binance wallet availability
  useEffect(() => {
    if (isBinanceAvailable) return;
    let mounted = true;
    waitForBinanceWallet(3000, 100).then((detected) => {
      if (mounted && detected) {
        setIsBinanceAvailable(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, [isBinanceAvailable]);

  const injectedConnector = connectors.find((c) => 
    c.id === "injected" || 
    c.type === "injected" ||
    (c as any).name?.toLowerCase().includes("injected")
  );

  // Determine connected wallet using comprehensive detection
  let detectedWallet: "Phantom" | "OneKey" | "OKX" | "Bitget" | "TokenPocket" | "Coinbase Wallet" | "Rainbow" | "Binance" | "MetaMask" | null = null;
  
  if (typeof window !== "undefined") {
    if ((window as any).$onekey?.ethereum || (window as any).ethereum?.isOneKey || (window as any).$onekey) {
      detectedWallet = "OneKey";
    } else if ((window as any).phantom?.ethereum || (window as any).ethereum?.isPhantom) {
      detectedWallet = "Phantom";
    } else {
      const hasOKXSolana = !!(window as any).okxwallet?.solana;
      const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
      const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
      
      if (hasOKXMultiChain && (window as any).ethereum) {
        detectedWallet = "OKX";
      } else if ((window as any).okxwallet?.ethereum ||
                 (window as any).ethereum?.isOKX ||
                 (window as any).okxwallet) {
        detectedWallet = "OKX";
      } else if (isBitgetProviderAvailable()) {
        detectedWallet = "Bitget";
      } else if (isTokenPocketProviderAvailable()) {
        detectedWallet = "TokenPocket";
      } else if (isCoinbaseWalletProviderAvailableSync()) {
        detectedWallet = "Coinbase Wallet";
      } else if ((window as any).ethereum?.isRainbow === true) {
        detectedWallet = "Rainbow";
      } else if ((window as any).BinanceChain || (window as any).ethereum?.isBinance) {
        detectedWallet = "Binance";
      } else if (isMetaMaskProviderAvailable()) {
        detectedWallet = "MetaMask";
      }
    }
  }

  const hasMatchingAddress =
    ownerAddress &&
    wagmiAddress &&
    ownerAddress.toLowerCase() === wagmiAddress.toLowerCase();

  // Check if Solana is connected
  const isSolanaConnected = (() => {
    if (connectionChainType !== "solana") return false;
    const oneKeySolanaProvider = getOneKeySolanaProvider();
    if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected && oneKeySolanaProvider.publicKey) {
      const solanaAddress = oneKeySolanaProvider.publicKey.toString();
      if (ownerAddress === solanaAddress) return true;
    }
    const okxSolanaProvider = getOKXSolanaProvider();
    if (okxSolanaProvider && okxSolanaProvider.isConnected && okxSolanaProvider.publicKey) {
      const solanaAddress = okxSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) return true;
    }
    const binanceSolanaProvider = getBinanceSolanaProvider();
    if (binanceSolanaProvider && binanceSolanaProvider.isConnected && binanceSolanaProvider.publicKey) {
      const solanaAddress = binanceSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) return true;
    }
    const bitgetSolanaProvider = getBitgetSolanaProvider();
    if (bitgetSolanaProvider && bitgetSolanaProvider.isConnected && bitgetSolanaProvider.publicKey) {
      const solanaAddress = bitgetSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) return true;
    }
    const coinbaseSolanaProvider = getCoinbaseWalletSolanaProvider();
    if (coinbaseSolanaProvider && coinbaseSolanaProvider.isConnected && coinbaseSolanaProvider.publicKey) {
      const solanaAddress = coinbaseSolanaProvider.publicKey?.toString();
      if (solanaAddress && ownerAddress === solanaAddress) return true;
    }
    const solanaProvider = getPhantomSolanaProvider();
    if (solanaProvider && solanaProvider.isConnected && solanaProvider.publicKey) {
      const solanaAddress = solanaProvider.publicKey.toString();
      if (ownerAddress === solanaAddress) return true;
    }
    if (ownerAddress && availableAddresses.length > 0) {
      const isSolanaAddress = !ownerAddress.startsWith("0x") && ownerAddress.length >= 32 && ownerAddress.length <= 44;
      const matchesAvailable = availableAddresses.some(addr => addr === ownerAddress);
      if (isSolanaAddress && matchesAvailable) return true;
    }
    return false;
  })();

  // Check if Bitcoin is connected
  const isBitcoinConnected = (() => {
    if (connectionChainType !== "bitcoin") return false;
    if (!ownerAddress) return false;
    const isBitcoinAddr = /^(bc1|[13]|[mn2])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(ownerAddress);
    return isBitcoinAddr;
  })();

  // Check if Ethereum wallet is connected
  const isEthereumConnected = (() => {
    if (connectionChainType !== "ethereum") return false;
    if (wagmiIsConnected && wagmiAddress) return true;
    if (ownerAddress && availableAddresses.length > 0) {
      const isEthereumAddress = ownerAddress.startsWith("0x") || ownerAddress.startsWith("0X");
      const matchesAvailable = availableAddresses.some(
        addr => addr.toLowerCase() === ownerAddress.toLowerCase()
      );
      return isEthereumAddress && matchesAvailable;
    }
    return false;
  })();

  const hasWalletExtensionConnection = isEthereumConnected || isSolanaConnected || isBitcoinConnected;
  const isWalletConnected = hasWalletExtensionConnection && ownerAddress;

  // Determine connected wallet name based on chain type and connection state
  const connectedWallet = (() => {
    if (isSolanaConnected) {
      const oneKeySolanaProvider = getOneKeySolanaProvider();
      if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected && ownerAddress) {
        const oneKeySolanaAddress = oneKeySolanaProvider.publicKey?.toString();
        if (oneKeySolanaAddress === ownerAddress) return "OneKey (Solana)";
      }
      const okxSolanaProvider = getOKXSolanaProvider();
      if (okxSolanaProvider && okxSolanaProvider.isConnected && ownerAddress) {
        const okxSolanaAddress = okxSolanaProvider.publicKey?.toString();
        if (okxSolanaAddress === ownerAddress) return "OKX (Solana)";
      }
      const bitgetSolanaProvider = getBitgetSolanaProvider();
      if (bitgetSolanaProvider && bitgetSolanaProvider.isConnected && ownerAddress) {
        const bitgetSolanaAddress = bitgetSolanaProvider.publicKey?.toString();
        if (bitgetSolanaAddress === ownerAddress) return "Bitget (Solana)";
      }
      const binanceSolanaProvider = getBinanceSolanaProvider();
      if (binanceSolanaProvider && binanceSolanaProvider.isConnected && ownerAddress) {
        const binanceSolanaAddress = binanceSolanaProvider.publicKey?.toString();
        if (binanceSolanaAddress === ownerAddress) return "Binance (Solana)";
      }
      return "Phantom (Solana)";
    }
    if (isBitcoinConnected) {
      const wallet = getPreferredBitcoinWallet();
      if (wallet) return `${wallet.name} (Bitcoin)`;
      return "Bitcoin Wallet";
    }
    if (isEthereumConnected) {
      if (isOneKeyDetected && isOneKeyEthereumAvailableSDK) return "OneKey";
      if (isOKXEthereumAvailable) return "OKX (Ethereum)";
      if (isBitgetAvailable) return "Bitget";
      if (isRainbowAvailable) return "Rainbow";
      if (isBinanceAvailable) return "Binance";
      if (isMetaMaskAvailable) return "MetaMask";
      if (isPhantomDetected) return "Phantom (Ethereum)";
    }
    return detectedWallet || "Wallet";
  })();

  // Helper functions to detect address types
  const isBitcoinAddress = (address: string | undefined | null): boolean => {
    if (!address || typeof address !== "string") return false;
    return /^(bc1|[13]|[mn2])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(address);
  };

  const isSolanaAddress = (address: string | undefined | null): boolean => {
    if (!address || typeof address !== "string") return false;
    return address.length >= 32 && address.length <= 44 && !address.startsWith("0x") && /^[1-9A-HJ-NP-Za-km-z]+$/.test(address);
  };

  const isEthereumAddress = (address: string | undefined | null): boolean => {
    if (!address || typeof address !== "string") return false;
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  // Auto-select single address when only one is available
  useEffect(() => {
    if (isBitcoinConnected && ownerAddress) return;
    if (isSolanaConnected && ownerAddress) return;
    
    if (availableAddresses.length === 1 && !ownerAddress) {
      const singleAddress = availableAddresses[0];
      if (singleAddress && typeof singleAddress === "string") {
        const isBitcoin = isBitcoinAddress(singleAddress);
        const isSolana = isSolanaAddress(singleAddress);
        const isEthereum = isEthereumAddress(singleAddress);
        
        if (
          (connectionChainType === "bitcoin" && isBitcoin) ||
          (connectionChainType === "solana" && isSolana) ||
          (connectionChainType === "ethereum" && isEthereum) ||
          (!connectionChainType || connectionChainType === "ethereum")
        ) {
          setOwnerAddress(singleAddress);
          if (!connectionChainType || connectionChainType === "ethereum") {
            if (isBitcoin) {
              setConnectionChainType("bitcoin");
            } else if (isSolana) {
              setConnectionChainType("solana");
            }
          }
        }
      }
    }
  }, [availableAddresses, ownerAddress, connectionChainType, isBitcoinConnected, isSolanaConnected]);

  // Sync ownerAddress with wagmiAddress when Ethereum wallet is connected
  const lastSyncedWagmiAddressRef = useRef<string | undefined>(undefined);
  
  useEffect(() => {
    if (connectionChainType !== "ethereum") return;

    if (ownerAddress) {
      if (isBitcoinAddress(ownerAddress)) return;
      if (isSolanaAddress(ownerAddress)) return;
    }

    if (wagmiIsConnected && wagmiAddress) {
      if (lastSyncedWagmiAddressRef.current !== wagmiAddress) {
        const currentOwnerAddress = ownerAddress?.toLowerCase() || "";
        const currentWagmiAddress = wagmiAddress.toLowerCase();
        if (currentOwnerAddress !== currentWagmiAddress) {
          if (!ownerAddress || isEthereumAddress(ownerAddress)) {
            setOwnerAddress(wagmiAddress);
            lastSyncedWagmiAddressRef.current = wagmiAddress;
          }
        } else {
          lastSyncedWagmiAddressRef.current = wagmiAddress;
        }
      }
    } else if (!wagmiIsConnected) {
      lastSyncedWagmiAddressRef.current = undefined;
    }
  }, [wagmiIsConnected, wagmiAddress, setOwnerAddress, connectionChainType, ownerAddress]);

  // Reset form when operation changes
  useEffect(() => {
    if (selectedOperation) {
      const config = OPERATION_CONFIGS[selectedOperation];
      const initialData: Record<string, string> = {};
      config.fields.forEach((field) => {
        initialData[field.key] = "";
      });
      setFormData(initialData);
      setSigningResult(null);
      // Reset connection method when operation changes
      setConnectionMethod(null);
      setOwnerAddress("");
      setAvailableAddresses([]);
    }
  }, [selectedOperation]);

  // Auto-generate deadline
  const generateDeadline = () => {
    const now = Math.floor(Date.now() / 1000);
    const oneHourLater = now + 3600;
    setDeadline(oneHourLater.toString());
  };

  // Auto-generate nonce
  const generateNonce = () => {
    setNonce(Date.now().toString());
  };

  // Generate owner address from mnemonic
  const generateOwnerAddress = async () => {
    if (!mnemonic.trim()) {
      alert("Please enter a mnemonic phrase");
      return;
    }

    try {
      const { mormSigner } = await import("../sdk");
      await mormSigner.initialize();
      const result = await mormSigner.generateAddress(mnemonic, 0);
      if (result.success && result.address) {
        setOwnerAddress(result.address);
      } else {
        alert(result.error || "Failed to generate address");
      }
    } catch (error: any) {
      alert(error.message || "Failed to generate address");
    }
  };

  // Wallet connection handlers (similar to AgentDelegation)
  const handleConnectMetaMask = async () => {
    if (!isMetaMaskAvailable) {
      alert("MetaMask wallet is not installed. Please install MetaMask wallet extension.");
      return;
    }

    try {
      const ethereum = (window as any).ethereum;
      if (ethereum) {
        const accounts = await ethereum.request({
          method: "eth_requestAccounts",
        });

        if (accounts && accounts.length > 0) {
          const validAddresses = accounts.filter((addr: any): addr is string => 
            addr && typeof addr === "string" && addr.trim().length > 0
          );
          if (validAddresses.length > 0) {
            setAvailableAddresses(validAddresses);
            if (validAddresses.length === 1) {
              setOwnerAddress(validAddresses[0]);
            }
          }
        }
      }

      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert("MetaMask connector not found. Please ensure MetaMask wallet is installed.");
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to MetaMask:", error);
        alert(`Failed to connect to MetaMask: ${error.message || "Unknown error"}`);
      }
    }
  };

  const handleConnectPhantom = async () => {
    if (connectionChainType === "solana") {
      try {
        if (!isPhantomSolanaAvailable()) {
          alert("Phantom Solana wallet is not available. Please ensure Phantom wallet is installed and unlocked.");
          return;
        }
        const solanaAddress = await connectPhantomSolana();
        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          setConnectionChainType("solana");
        }
      } catch (error: any) {
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(`Failed to connect to Phantom Solana: ${error.message || "Unknown error"}`);
        }
      }
      return;
    }

    if (!isPhantomDetected) {
      alert("Phantom wallet is not installed. Please install Phantom wallet extension.");
      return;
    }

    try {
      let phantomProvider = (window as any).phantom?.ethereum;
      if (!phantomProvider && (window as any).phantom) {
        const phantom = (window as any).phantom;
        if (typeof phantom.request === "function") {
          phantomProvider = phantom;
        }
      }
      if (!phantomProvider && (window as any).ethereum?.isPhantom) {
        phantomProvider = (window as any).ethereum;
      }

      if (!phantomProvider) {
        alert("Phantom wallet Ethereum provider not found. Please ensure Phantom wallet is installed and switched to Ethereum mode.");
        return;
      }

      const accounts = await phantomProvider.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        setAvailableAddresses(accounts);
        if (accounts.length === 1) {
          setOwnerAddress(accounts[0]);
        }
      }

      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Phantom:", error);
        alert(`Failed to connect to Phantom: ${error.message || "Unknown error"}`);
      }
    }
  };

  const handleConnectOneKey = async () => {
    if (connectionChainType === "solana") {
      try {
        if (!isOneKeySolanaAvailable) {
          alert("OneKey Solana wallet is not available. Please ensure OneKey wallet is installed and unlocked.");
          return;
        }
        const solanaAddress = await connectOneKeySolana();
        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          setConnectionChainType("solana");
        }
      } catch (error: any) {
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(`Failed to connect to OneKey Solana: ${error.message || "Unknown error"}`);
        }
      }
      return;
    }

    if (connectionChainType === "bitcoin") {
      try {
        if (!isOneKeyBitcoinAvailable) {
          alert("OneKey Bitcoin wallet is not available. Please ensure OneKey wallet is installed and switched to Bitcoin network.");
          return;
        }
        const bitcoinAddress = await getOneKeyBitcoinAddress();
        if (bitcoinAddress && typeof bitcoinAddress === "string") {
          setAvailableAddresses([bitcoinAddress]);
          setOwnerAddress(bitcoinAddress);
          setConnectionChainType("bitcoin");
        }
      } catch (error: any) {
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Bitcoin connection request.");
        } else {
          alert(`Failed to connect to OneKey Bitcoin: ${error.message || "Unknown error"}`);
        }
      }
      return;
    }

    if (!isOneKeyDetected) {
      alert("OneKey wallet is not installed. Please install OneKey wallet extension.");
      return;
    }

    try {
      const oneKeyProvider = (window as any).$onekey?.ethereum || (window as any).ethereum;
      if (!oneKeyProvider) {
        alert("OneKey wallet provider not found. Please ensure OneKey wallet is installed and enabled.");
        return;
      }

      const accounts = await oneKeyProvider.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any) => addr && typeof addr === "string") as string[];
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to OneKey:", error);
        alert(`Failed to connect to OneKey: ${error.message || "Unknown error"}`);
      }
    }
  };

  const handleConnectBitget = async () => {
    // Handle Solana connection
    if (connectionChainType === "solana") {
      try {
        if (!isBitgetSolanaAvailable()) {
          alert(
            "Bitget Solana wallet is not available. Please ensure Bitget wallet is installed and unlocked.",
          );
          return;
        }
        const solanaAddress = await connectBitgetSolana();
        if (solanaAddress && typeof solanaAddress === "string") {
          setAvailableAddresses([solanaAddress]);
          setOwnerAddress(solanaAddress);
          setConnectionChainType("solana");
        }
      } catch (error: any) {
        if (error.code === 4001 || error.message?.includes("rejected")) {
          alert("User rejected the Solana connection request.");
        } else {
          alert(
            `Failed to connect to Bitget Solana: ${error.message || "Unknown error"}`,
          );
        }
      }
      return;
    }

    // Handle Bitcoin connection
    if (connectionChainType === "bitcoin") {
      try {
        // Check if Bitget Bitcoin (unisat) is available
        // Per analysis: Bitget uses window.bitkeep.unisat (primary) or may shim window.unisat
        const bitkeep = (window as any).bitkeep;
        const hasBitgetWallet = !!(bitkeep?.isBitKeep || bitkeep?.ethereum?.isBitEthereum);
        
        // Primary: Check window.bitkeep.unisat (Bitget's native Bitcoin provider)
        // Secondary: Check window.unisat shim (only if Bitget is detected via other means)
        const unisatProvider = bitkeep?.unisat || 
          (hasBitgetWallet && !(window as any).$onekey && (window as any).unisat);
        
        if (!unisatProvider) {
          alert(
            "Bitget Bitcoin wallet is not available. Please ensure:\n" +
            "1. Bitget wallet extension is unlocked\n" +
            "2. Bitget wallet is switched to Bitcoin network\n" +
            "3. You have a Bitcoin account in Bitget\n\n" +
            "After switching to Bitcoin network in Bitget, refresh this page and try again.",
          );
          return;
        }

        // Request accounts from Bitget's unisat provider
        const accounts = await unisatProvider.requestAccounts();
        
        if (accounts && accounts.length > 0) {
          const address = accounts[0];
          setAvailableAddresses(accounts);
          setOwnerAddress(address);
        } else {
          throw new Error("No accounts returned from Bitget Bitcoin wallet");
        }
      } catch (error: any) {
        if (error.code === 4001 || error.message?.includes("rejected") || error.message?.includes("User rejected")) {
          alert("User rejected the Bitcoin connection request.");
        } else {
          alert(
            `Failed to connect to Bitget Bitcoin: ${error.message || "Unknown error"}\n\n` +
            "Please ensure:\n" +
            "1. Bitget wallet extension is unlocked\n" +
            "2. Bitget wallet is switched to Bitcoin network (not Ethereum)\n" +
            "3. You have a Bitcoin account in Bitget\n\n" +
            "After switching to Bitcoin network in Bitget, refresh this page and try again.",
          );
        }
      }
      return;
    }

    // Handle Ethereum connection
    // Check availability
    if (!isBitgetAvailable) {
      const detected: boolean = await waitForBitgetWallet(3000, 100);
      if (!detected) {
        alert(
          "Bitget wallet is not installed. Please install Bitget wallet extension.",
        );
        return;
      }
    }

    try {
      // Use the helper function to get Bitget provider
      let bitgetProvider = getBitgetProvider();

      // If not immediately available, wait for it with polling
      if (!bitgetProvider) {
        const detected: boolean = await waitForBitgetWallet(3000, 100);
        if (detected) {
          bitgetProvider = getBitgetProvider();
        }
      }

      if (!bitgetProvider) {
        alert(
          "Bitget wallet provider not found. Please ensure Bitget wallet is installed and enabled.",
        );
        return;
      }

      // Request account access from Bitget directly
      // This will trigger the Bitget wallet popup
      const accounts = await bitgetProvider.request({
        method: "eth_requestAccounts",
      });

      // Store all available addresses
      if (accounts && accounts.length > 0) {
        const validAddresses = accounts.filter((addr: any): addr is string => 
          addr && typeof addr === "string" && addr.trim().length > 0
        );
        if (validAddresses.length > 0) {
          setAvailableAddresses(validAddresses);
          // If only one address, auto-select it
          if (validAddresses.length === 1) {
            setOwnerAddress(validAddresses[0]);
          }
        }
      }

      // After Bitget is connected, connect via wagmi
      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        // Wait for wagmi to update
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        alert(
          "Bitget connector not found. Please ensure Bitget wallet is installed.",
        );
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request.");
      } else {
        console.error("Error connecting to Bitget:", error);
        alert(
          `Failed to connect to Bitget: ${error.message || "Unknown error"}`,
        );
      }
    }
  };

  // Smart connect handler
  const getBestWallet = (): { wallet: string; handler: () => Promise<void> } | null => {
    const bestWalletName = getBestWalletName(connectionChainType);
    if (!bestWalletName) return null;

    const walletHandlerMap: Partial<Record<string, () => Promise<void>>> = {
      "OneKey": handleConnectOneKey,
      "OKX": async () => {
        // OKX connection would go here - simplified for now
        alert("OKX wallet connection not yet implemented in FunctionalSigner");
      },
      "Bitget": handleConnectBitget,
      "TokenPocket": async () => {
        // TokenPocket connection would go here - simplified for now
        alert("TokenPocket wallet connection not yet implemented in FunctionalSigner");
      },
      "Coinbase Wallet": async () => {
        // Coinbase Wallet connection would go here - simplified for now
        alert("Coinbase Wallet connection not yet implemented in FunctionalSigner");
      },
      "Rainbow": async () => {
        // Rainbow connection would go here - simplified for now
        alert("Rainbow wallet connection not yet implemented in FunctionalSigner");
      },
      "Binance": async () => {
        // Binance connection would go here - simplified for now
        alert("Binance wallet connection not yet implemented in FunctionalSigner");
      },
      "MetaMask": handleConnectMetaMask,
      "Phantom": handleConnectPhantom,
    };

    const handler = walletHandlerMap[bestWalletName];
    if (handler) {
      return { wallet: bestWalletName, handler };
    }

    return null;
  };

  const handleSmartConnect = async () => {
    const bestWallet = getBestWallet();
    if (!bestWallet) {
      if (connectionChainType === "ethereum") {
        alert("No Ethereum wallet detected. Please install MetaMask, Phantom, OneKey, OKX, Bitget, or Binance wallet.");
      } else if (connectionChainType === "solana") {
        alert("No Solana wallet detected. Please install Phantom, OneKey, OKX, Bitget, or Binance wallet extension for Solana support.");
      } else if (connectionChainType === "bitcoin") {
        alert("No Bitcoin wallet detected. Please install OneKey, OKX, Bitget, Unisat, or Xverse wallet extension.");
      }
      return;
    }
    await bestWallet.handler();
  };

  // Build payload from form data
  const buildPayload = (): Record<string, any> => {
    if (!selectedOperation) {
      throw new Error("No operation selected");
    }

    const config = OPERATION_CONFIGS[selectedOperation];
    const payload: Record<string, any> = {
      action: config.action,
    };

    // Add address field for operations that need it
    if (
      selectedOperation === "cancelOrder" ||
      selectedOperation === "changeLeverage" ||
      selectedOperation === "changeMarginRatio" ||
      selectedOperation === "internTransfer"
    ) {
      payload.address = ownerAddress;
    }

    // Process each field
    config.fields.forEach((field) => {
      const value = formData[field.key];
      if (value === undefined || value === "") {
        if (field.required) {
          throw new Error(`${field.label} is required`);
        }
        return;
      }

      let processedValue: any = value;

      // Convert to satoshi if needed
      if (field.convertToSatoshi && field.type === "number") {
        processedValue = toSatoshi(parseFloat(value));
      }

      // Handle special field mappings
      if (selectedOperation === "bucketTransfer" && field.key === "assetId") {
        payload.asset = processedValue; // Use "asset" not "assetId"
      } else if (
        (selectedOperation === "extractToBucket" ||
          selectedOperation === "withdrawFromBucket" ||
          selectedOperation === "bankMint" ||
          selectedOperation === "bankTransfer") &&
        field.key === "assetId"
      ) {
        payload.asset = processedValue; // Use "asset" not "assetId"
      } else if (selectedOperation === "closePosition") {
        if (field.key === "quantity") {
          payload.closeSize = processedValue;
        } else if (field.key === "marketPrice") {
          payload.marketPrice = processedValue;
        } else {
          payload[field.key] = processedValue;
        }
      } else if (selectedOperation === "placeOrder") {
        if (field.key === "side") {
          payload.side = value.toUpperCase();
        } else if (field.key === "orderType") {
          payload.orderType = value.toUpperCase();
        } else {
          payload[field.key] = processedValue;
        }
      } else {
        payload[field.key] = processedValue;
      }
    });

    return payload;
  };

  // Prepare EIP-712 typed data
  const prepareTypedData = (payload: Record<string, any>) => {
    const domain = {
      name: "Morpheum",
      version: "1",
      chainId: chainId,
      verifyingContract:
        "0x0000000000000000000000000000000000000000" as `0x${string}`,
    };

    // Build types dynamically based on payload
    const types: Record<string, any[]> = {
      EIP712Domain: [
        { name: "name", type: "string" },
        { name: "version", type: "string" },
        { name: "chainId", type: "uint256" },
        { name: "verifyingContract", type: "address" },
      ],
      Message: [] as any[],
    };

    // Add fields to Message type
    Object.keys(payload).forEach((key) => {
      if (key === "action") {
        types.Message.push({ name: "action", type: "string" });
      } else if (key === "owner") {
        types.Message.push({ name: "owner", type: "address" });
      } else if (key === "nonce") {
        types.Message.push({ name: "nonce", type: "string" });
      } else if (key === "deadline") {
        types.Message.push({ name: "deadline", type: "uint256" });
      } else if (key === "address") {
        types.Message.push({ name: "address", type: "address" });
      } else if (
        typeof payload[key] === "string" &&
        payload[key].startsWith("0x")
      ) {
        types.Message.push({ name: key, type: "address" });
      } else if (
        typeof payload[key] === "number" ||
        /^\d+$/.test(payload[key])
      ) {
        types.Message.push({ name: key, type: "uint256" });
      } else {
        types.Message.push({ name: key, type: "string" });
      }
    });

    const message: Record<string, any> = {};
    Object.keys(payload).forEach((key) => {
      if (
        key === "owner" ||
        key === "address" ||
        (key.startsWith("0x") && key.length === 42)
      ) {
        message[key] = payload[key].toLowerCase();
      } else {
        message[key] = payload[key];
      }
    });

    return {
      domain,
      types,
      primaryType: "Message" as const,
      message,
    };
  };

  // Get private key from mnemonic (similar to BaseSignatureBuilder)
  const getPrivateKeyHex = async (): Promise<string> => {
    if (!mnemonic.trim()) {
      throw new Error("Mnemonic is required for WASM signing");
    }

    // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
    const masterNode = ethers.HDNodeWallet.fromPhrase(
      mnemonic.trim(),
      "", // no passphrase
      "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
    );

    // Validate that derived address matches
    if (masterNode.address.toLowerCase() !== ownerAddress.toLowerCase()) {
      throw new Error(
        `Address mismatch: derived ${masterNode.address} does not match ${ownerAddress}`,
      );
    }

    // Extract private key in the exact format Go's crypto.ToECDSA expects
    const privateKeyBigInt = BigInt(masterNode.privateKey);
    const SECP256K1_ORDER = BigInt(
      "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
    );

    if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
      throw new Error(
        `Invalid private key: must be between 1 and secp256k1 order - 1`,
      );
    }

    // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
    const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
    const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

    if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
      throw new Error(
        `Invalid private key format: must be 64 lowercase hex characters (0-9, a-f) without 0x prefix`,
      );
    }

    return paddedHex;
  };

  // Handle signing with wallet
  const handleSign = async () => {
    if (!selectedOperation) {
      alert("Please select an operation first");
      return;
    }

    if (!ownerAddress) {
      alert("Please set owner address");
      return;
    }

    if (!nonce) {
      alert("Please generate nonce");
      return;
    }

    if (!deadline) {
      alert("Please generate deadline");
      return;
    }

    setIsSigning(true);
    setSigningResult(null);

    try {
      // Build payload
      const payload = buildPayload();

      // Add owner, nonce, deadline
      payload.owner = ownerAddress.toLowerCase();
      payload.nonce = nonce;
      payload.deadline = deadline;

      // Prepare typed data
      const typedData = prepareTypedData(payload);

      // Sign with wallet
      const signature = await signTypedDataAsync({
        domain: typedData.domain as any,
        types: typedData.types as any,
        primaryType: typedData.primaryType,
        message: typedData.message as any,
      });

      setSigningResult({
        success: true,
        signature,
        typedData: {
          payload,
          typedData,
        },
      });
    } catch (error: any) {
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setSigningResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        console.error("Error signing:", error);
        setSigningResult({
          success: false,
          error: error.message || "Unknown error during signing",
        });
      }
    } finally {
      setIsSigning(false);
    }
  };

  // Handle signing with WASM
  const handleSignWasm = async () => {
    if (!selectedOperation) {
      alert("Please select an operation first");
      return;
    }

    if (!ownerAddress) {
      alert("Please set owner address");
      return;
    }

    if (!nonce) {
      alert("Please generate nonce");
      return;
    }

    if (!deadline) {
      alert("Please generate deadline");
      return;
    }

    if (!mnemonic.trim()) {
      alert("Please enter mnemonic for WASM signing");
      return;
    }

    // Check if WASM is ready
    if (!mormSigner.isReady()) {
      try {
        await mormSigner.initialize();
        if (!mormSigner.isReady()) {
          alert("WASM module failed to initialize");
          return;
        }
      } catch (error: any) {
        alert(`Failed to initialize WASM: ${error.message || "Unknown error"}`);
        return;
      }
    }

    setIsSigningWasm(true);
    setSigningResult(null);

    try {
      // Build payload
      const payload = buildPayload();

      // Add owner, nonce, deadline
      payload.owner = ownerAddress.toLowerCase();
      payload.nonce = nonce;
      payload.deadline = deadline;

      // Get private key from mnemonic
      const privateKeyHex = await getPrivateKeyHex();

      // Build EIP712Tx structure for generateSingleSignature
      const now = Math.floor(Date.now() / 1000);
      const deadlineNum = parseInt(deadline, 10);

      const eip712Tx: Record<string, any> = {
        txId: "", // Empty txId for signing
        sigType: "ECDSA_LEGACY", // Signature type as string
        threshold: 1,
        timestamp: now,
        payload: payload,
        deadline: deadlineNum,
      };

      // Use generateSingleSignature to sign the EIP712Tx
      const result = await mormSigner.generateSingleSignature(
        JSON.stringify(eip712Tx),
        privateKeyHex,
        ownerAddress.toLowerCase(),
      );

      if (!result.success || !result.signature) {
        throw new Error(result.error || "Failed to generate signature");
      }

      // Prepare typed data for display
      const typedData = prepareTypedData(payload);

      setSigningResult({
        success: true,
        signature: result.signature,
        typedData: {
          payload,
          typedData,
          eip712Tx,
        },
      });
    } catch (error: any) {
      console.error("Error signing with WASM:", error);
      setSigningResult({
        success: false,
        error: error.message || "Unknown error during WASM signing",
      });
    } finally {
      setIsSigningWasm(false);
    }
  };

  const config = selectedOperation
    ? OPERATION_CONFIGS[selectedOperation]
    : null;

  return (
    <div className="component">
      <h3>Functional Signer</h3>
      <p>Sign EIP-712 messages for Morpheum protocol operations</p>

      {/* Step 1: Select Operation */}
      <div
        className={`agent-delegation-step ${selectedOperation ? "connected" : ""}`}
      >
        <h4>Step 1: Select Operation {selectedOperation && "✓"}</h4>

        {!selectedOperation ? (
          <div className="agent-delegation-wallet-buttons">
            {Object.entries(OPERATION_CONFIGS).map(([key, config]) => (
              <button
                key={key}
                className="button"
                onClick={() => setSelectedOperation(key as OperationType)}
              >
                {config.label}
              </button>
            ))}
          </div>
        ) : (
          <div>
            <p style={{ marginBottom: "1rem" }}>
              <strong>Selected:</strong> {config?.label}
            </p>
            <button
              className="button secondary"
              onClick={() => {
                setSelectedOperation(null);
                setFormData({});
                setSigningResult(null);
                setConnectionMethod(null);
                setOwnerAddress("");
                setAvailableAddresses([]);
              }}
            >
              Change Operation
            </button>
          </div>
        )}
      </div>

      {/* Step 1.5: Select Connection Method */}
      {selectedOperation ? (
        <div
          className={`agent-delegation-step ${connectionMethod ? "connected" : ""}`}
        >
          <h4>Step 2: Select Connection Method {connectionMethod && "✓"}</h4>

          {!connectionMethod ? (
            <div className="agent-delegation-wallet-buttons">
              <button
                className="button"
                onClick={() => setConnectionMethod("wallet")}
              >
                Connect Wallet
              </button>
              <button
                className="button"
                onClick={() => setConnectionMethod("mnemonic")}
              >
                Use Local Mnemonic
              </button>
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: "1rem" }}>
                <strong>Selected:</strong> {connectionMethod === "wallet" ? "Connect Wallet" : "Use Local Mnemonic"}
              </p>
              <button
                className="button secondary"
                onClick={() => {
                  setConnectionMethod(null);
                  setOwnerAddress("");
                  setAvailableAddresses([]);
                }}
              >
                Change Method
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 2: Select Connection Method</h4>
          <p className="agent-delegation-disabled-text">
            Please select an operation in Step 1 to continue.
          </p>
        </div>
      )}

      {/* Step 3: Configure Parameters */}
      {selectedOperation && connectionMethod ? (
        <div
          className={`agent-delegation-step ${
            ownerAddress && nonce && deadline ? "connected" : ""
          }`}
        >
          <h4>
            Step 3: Configure Parameters{" "}
            {ownerAddress && nonce && deadline && "✓"}
          </h4>

          {/* Wallet Connection - only show if wallet method is selected */}
          {connectionMethod === "wallet" && (
            <div className="form-group">
              <label htmlFor="walletConnection">Wallet Connection:</label>
              {!isWalletConnected ? (
              <div>
                {/* Chain Type Selector */}
                <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                  <label htmlFor="connectionChainType" style={{ marginBottom: "0.5rem", display: "block" }}>
                    <strong>Select Chain Type:</strong>
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                      className={`button ${connectionChainType === "ethereum" ? "" : "secondary"}`}
                      onClick={() => setConnectionChainType("ethereum")}
                      style={{ flex: 1, minWidth: "100px" }}
                    >
                      Ethereum
                    </button>
                    {(isPhantomSolanaAvailable() || isOneKeySolanaAvailable || isOKXSolanaSDKAvailable() || isBinanceSolanaAvailable()) && (
                      <button
                        className={`button ${connectionChainType === "solana" ? "" : "secondary"}`}
                        onClick={() => setConnectionChainType("solana")}
                        style={{ flex: 1, minWidth: "100px" }}
                      >
                        Solana
                      </button>
                    )}
                    {(isBitcoinWalletAvailable() || isOKXWalletInstalled()) && (
                      <button
                        className={`button ${connectionChainType === "bitcoin" ? "" : "secondary"}`}
                        onClick={() => setConnectionChainType("bitcoin")}
                        style={{ flex: 1, minWidth: "100px" }}
                      >
                        Bitcoin
                      </button>
                    )}
                  </div>
                  <small className="agent-delegation-small-text" style={{ display: "block", marginTop: "0.5rem" }}>
                    {connectionChainType === "ethereum"
                      ? "Connect to Ethereum network (MetaMask, Phantom Ethereum, OneKey, OKX, Binance)"
                      : connectionChainType === "solana"
                        ? "Connect to Solana network (Phantom Solana, OneKey Solana, OKX Solana, Binance Solana)"
                        : "Connect to Bitcoin network (OneKey, Unisat, Xverse, OKX). If using OKX, ensure it's switched to Bitcoin network."}
                  </small>
                </div>

                {/* Smart Connect Button */}
                <div className="agent-delegation-wallet-buttons">
                  {(() => {
                    const bestWallet = getBestWallet();
                    if (bestWallet) {
                      return (
                        <button
                          className="button"
                          onClick={handleSmartConnect}
                          disabled={isConnecting || (connectionChainType === "ethereum" && !injectedConnector)}
                        >
                          {isConnecting
                            ? "Connecting..."
                            : `Connect ${bestWallet.wallet}${connectionChainType !== "ethereum" ? ` (${connectionChainType.charAt(0).toUpperCase() + connectionChainType.slice(1)})` : ""}`}
                        </button>
                      );
                    } else {
                      if (connectionChainType === "ethereum") {
                        return (
                          <p className="agent-delegation-error-message">
                            No Ethereum wallet detected. Please install MetaMask, Phantom, OneKey, OKX, Rainbow, or Binance wallet.
                          </p>
                        );
                      } else if (connectionChainType === "solana") {
                        return (
                          <p className="agent-delegation-error-message">
                            No Solana wallet detected. Please install Phantom, OneKey, OKX, Bitget, or Binance wallet extension for Solana support.
                          </p>
                        );
                      } else if (connectionChainType === "bitcoin") {
                        return (
                          <p className="agent-delegation-error-message">
                            {isOKXWalletInstalled()
                              ? "Wallet detected, but Bitcoin provider is not available. Please switch your wallet to Bitcoin network in the extension, then refresh this page."
                              : "No Bitcoin wallet detected. Please install OneKey, OKX, Bitget, Unisat, or Xverse wallet extension."}
                          </p>
                        );
                      }
                      return null;
                    }
                  })()}
                </div>

                {availableAddresses.length > 0 && !ownerAddress && (
                  <div style={{ marginTop: "1rem" }}>
                    <label>Select Address:</label>
                    {availableAddresses.map((address) => (
                      <button
                        key={address}
                        className="button secondary"
                        onClick={() => setOwnerAddress(address)}
                        style={{
                          display: "block",
                          marginTop: "0.5rem",
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                        }}
                      >
                        {address}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="agent-delegation-connected-info">
                <div>
                  <strong>Connected:</strong> {connectedWallet}
                  {ownerAddress && (
                    <div className="agent-delegation-address">
                      Address: {ownerAddress}
                    </div>
                  )}
                </div>
                <button
                  className="button secondary"
                  onClick={async () => {
                    // Disconnect based on chain type
                    if (connectionChainType === "solana") {
                      // Try OneKey Solana first
                      const oneKeySolanaProvider = getOneKeySolanaProvider();
                      if (oneKeySolanaProvider && oneKeySolanaProvider.isConnected) {
                        try {
                          await oneKeySolanaProvider.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting OneKey Solana:", error);
                        }
                      }
                      // Try Phantom Solana
                      const solanaProvider = getPhantomSolanaProvider();
                      if (solanaProvider && solanaProvider.isConnected) {
                        try {
                          await solanaProvider.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting Phantom Solana:", error);
                        }
                      }
                      // Try OKX Solana
                      const okxSolanaProvider = getOKXSolanaProvider();
                      if (okxSolanaProvider && okxSolanaProvider.isConnected) {
                        try {
                          await okxSolanaProvider.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting OKX Solana:", error);
                        }
                      }
                      // Try Bitget Solana
                      const bitgetSolanaProvider = getBitgetSolanaProvider();
                      if (bitgetSolanaProvider && bitgetSolanaProvider.isConnected) {
                        try {
                          await bitgetSolanaProvider.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting Bitget Solana:", error);
                        }
                      }
                      // Try Binance Solana
                      const binanceSolanaProvider = getBinanceSolanaProvider();
                      if (binanceSolanaProvider && binanceSolanaProvider.isConnected) {
                        try {
                          await binanceSolanaProvider.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting Binance Solana:", error);
                        }
                      }
                    } else if (connectionChainType === "bitcoin") {
                      // Bitcoin wallets don't have a standard disconnect method
                      // Just clear the state
                    } else if (connectionChainType === "ethereum") {
                      disconnect();
                    }
                    // Clear state
                    setAvailableAddresses([]);
                    setOwnerAddress("");
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}
            </div>
          )}

          {/* Mnemonic Input - only show if mnemonic method is selected */}
          {connectionMethod === "mnemonic" && (
            <>
              <div className="form-group">
                <label htmlFor="mnemonic">Mnemonic (for address generation):</label>
                <textarea
                  id="mnemonic"
                  value={mnemonic}
                  onChange={(e) => setMnemonic(e.target.value)}
                  placeholder="Enter mnemonic to generate address..."
                  rows={2}
                />
                <button
                  className="button secondary"
                  onClick={generateOwnerAddress}
                  style={{ marginTop: "0.5rem" }}
                >
                  Generate Address from Mnemonic
                </button>
              </div>

              {/* Owner Address */}
              <div className="form-group">
                <label htmlFor="ownerAddress">Owner Address:</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    id="ownerAddress"
                    type="text"
                    value={ownerAddress}
                    onChange={(e) => setOwnerAddress(e.target.value)}
                    placeholder="0x..."
                    style={{ flex: 1 }}
                  />
                  <button
                    className="button secondary"
                    onClick={generateOwnerAddress}
                    disabled={!mnemonic.trim()}
                  >
                    Generate
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Nonce */}
          <div className="form-group">
            <label htmlFor="nonce">Nonce:</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="nonce"
                type="text"
                value={nonce}
                onChange={(e) => setNonce(e.target.value)}
                placeholder="Auto-generated timestamp"
              />
              <button className="button secondary" onClick={generateNonce}>
                Generate
              </button>
            </div>
          </div>

          {/* Deadline */}
          <div className="form-group">
            <label htmlFor="deadline">Deadline (Unix timestamp):</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="deadline"
                type="text"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                placeholder="Auto-generated (1 hour from now)"
              />
              <button className="button secondary" onClick={generateDeadline}>
                Generate
              </button>
            </div>
          </div>

          {/* Operation-specific fields */}
          {config && (
            <>
              <h5 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
                Operation Parameters:
              </h5>
              {config.fields.map((field) => (
                <div key={field.key} className="form-group">
                  <label htmlFor={field.key}>
                    {field.label}
                    {field.required && " *"}
                  </label>
                  {field.type === "select" ? (
                    <select
                      id={field.key}
                      value={formData[field.key] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        })
                      }
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={field.key}
                      type={field.type}
                      value={formData[field.key] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        })
                      }
                      placeholder={field.placeholder}
                    />
                  )}
                  {field.convertToSatoshi && (
                    <small className="agent-delegation-small-text">
                      Will be converted to satoshi format
                    </small>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 3: Configure Parameters</h4>
          <p className="agent-delegation-disabled-text">
            Please complete Step 2 to continue.
          </p>
        </div>
      )}

      {/* Step 4: Sign */}
      {selectedOperation && connectionMethod && ownerAddress && nonce && deadline ? (
        <div className="agent-delegation-step">
          <h4>Step 4: Sign Message</h4>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              className="button"
              onClick={handleSign}
              disabled={
                isSigning ||
                isSigningWasm ||
                !ownerAddress ||
                !nonce ||
                !deadline ||
                (config?.fields.some((f) => f.required && !formData[f.key]) ??
                  false)
              }
            >
              {isSigning ? "Signing..." : "Sign from Owner"}
            </button>
            <button
              className="button"
              onClick={handleSignWasm}
              disabled={
                isSigning ||
                isSigningWasm ||
                !ownerAddress ||
                !nonce ||
                !deadline ||
                !mnemonic.trim() ||
                (config?.fields.some((f) => f.required && !formData[f.key]) ??
                  false)
              }
            >
              {isSigningWasm ? "Signing..." : "Sign by Agent"}
            </button>
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 4: Sign Message</h4>
          <p className="agent-delegation-disabled-text">
            Please complete Step 3 to continue.
          </p>
        </div>
      )}

      {/* Result */}
      {signingResult && (
        <div
          className={`output ${signingResult.success ? "success" : "error"}`}
        >
          {signingResult.success ? (
            <>
              <strong>Signature:</strong>
              <br />
              {signingResult.signature}
              <br />
              <br />
              {signingResult.typedData && (
                <>
                  <strong>Payload & Typed Data:</strong>
                  <br />
                  <pre className="agent-delegation-pre">
                    {JSON.stringify(signingResult.typedData, null, 2)}
                  </pre>
                </>
              )}
            </>
          ) : (
            <>
              <strong>Error:</strong>
              <br />
              {signingResult.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default FunctionalSigner;
